import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO from "../components/SEO";
import { CASE_STUDIES } from "../caseStudies";
import { whatsappLink } from "../content";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES[slug];
  if (!cs) return <Navigate to="/work" replace />;

  return (
    <div data-testid="case-study-page">
      <SEO
        title={`${cs.client}, ${cs.tag} | The Next Design`}
        description={`${cs.client}: ${cs.title} A case study by The Next Design.`}
      />

      <section className="border-b border-neutral-200 blueprint-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Reveal>
            <Link to="/work" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-neutral-500 hover:text-brand-red transition-colors mb-10" data-testid="case-study-back-link">
              <ArrowLeft size={14} /> Back to Work
            </Link>
            <SectionTag>{cs.tag}</SectionTag>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98] max-w-4xl" data-testid="case-study-heading">
              {cs.title}
            </h1>
            <p className="mt-6 text-sm font-bold uppercase tracking-[0.25em] text-neutral-500" data-testid="case-study-client">
              Client, {cs.client}
            </p>
            <p className="mt-6 text-lg text-neutral-600 max-w-2xl leading-relaxed">{cs.intro}</p>
          </Reveal>
        </div>
      </section>

      {cs.hero && (
        <section className="border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <Reveal>
              <div className="img-zoom border border-neutral-200" data-testid="case-study-hero-image">
                <img src={cs.hero} alt={cs.heroAlt} className="w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {cs.video && (
        <section className="border-b border-neutral-200">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
            <Reveal>
              <div className="aspect-video border border-neutral-900" data-testid="case-study-video">
                <iframe
                  src={cs.video}
                  title={`${cs.client}, Virtual Plant Visit`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><SectionTag>{cs.challengeTitle}</SectionTag></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-3xl" data-testid="case-study-challenge">{cs.challenge}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <Reveal>
            <SectionTag>{cs.didTitle}</SectionTag>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl" data-testid="case-study-did-intro">{cs.didIntro}</p>
          </Reveal>
          {cs.did.length > 0 && (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {cs.did.map((d, i) => (
                <Reveal key={d.title} delay={i * 0.08}>
                  <div className="bg-white border border-neutral-200 hover:border-neutral-950 transition-colors h-full" data-testid={`case-study-deliverable-${i}`}>
                    <div className="aspect-[4/3] flex items-center justify-center p-4 overflow-hidden bg-white">
                      <img src={d.image} alt={d.title} className="max-w-full max-h-full object-contain" loading="lazy" />
                    </div>
                    <div className="p-6 border-t border-neutral-100">
                      <h3 className="font-display text-xl font-semibold tracking-tight">{d.title}</h3>
                      <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{d.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-brand-red mb-8">The Outcome</p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-4">
                <p className="font-display text-7xl sm:text-8xl font-semibold text-brand-red leading-none" data-testid="case-study-outcome-stat">{cs.outcomeStat}</p>
                <p className="mt-4 text-sm uppercase tracking-[0.2em] text-neutral-400">{cs.outcomeLabel}</p>
              </div>
              <div className="lg:col-span-8">
                <p className="text-lg text-neutral-300 leading-relaxed" data-testid="case-study-outcome-text">{cs.outcomeText}</p>
              </div>
            </div>
            <div className="mt-14">
              <a
                href={whatsappLink(`Hi Nitin, I read the ${cs.client} case study and I'd like to discuss a project.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-whatsapp-button"
                className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors inline-flex items-center gap-2"
              >
                Discuss a Similar Project <MessageCircle size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
